import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';

const ACCOUNT_CATEGORIES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];
const ENTRY_TYPES = ['DEBIT', 'CREDIT'];

const createBlankLine = (entryType = 'DEBIT') => ({
  accountId: '',
  entryType,
  amount: '',
  description: ''
});

const todayValue = () => new Date().toISOString().slice(0, 10);

const formatCurrency = (value) => {
  const amount = Number(value || 0);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount);
};

const amountValue = (value) => {
  const num = Number(value || 0);
  return Number.isFinite(num) ? num : 0;
};

export default function AccountingWorkspace() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState({
    totalAccounts: 0,
    totalJournalEntries: 0,
    postedDebitTotal: 0,
    postedCreditTotal: 0,
    accounts: [],
    recentEntries: []
  });
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [postingJournal, setPostingJournal] = useState(false);
  const [ledgerModal, setLedgerModal] = useState({ open: false, loading: false, data: null });

  const [accountForm, setAccountForm] = useState({
    name: '',
    accountCode: '',
    category: 'ASSET',
    parentGroup: '',
    description: ''
  });

  const [journalForm, setJournalForm] = useState({
    entryDate: todayValue(),
    description: '',
    referenceType: '',
    referenceId: '',
    lines: [createBlankLine('DEBIT'), createBlankLine('CREDIT')]
  });

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = () => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/accounting/dashboard`)
      .then((res) => setDashboard(res.data))
      .catch((err) => {
        toast.error(`Failed to load accounting workspace: ${err.response?.data?.message || err.message}`);
      })
      .finally(() => setLoading(false));
  };

  const updateAccountForm = (field, value) => {
    setAccountForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateJournalForm = (field, value) => {
    setJournalForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateLine = (index, field, value) => {
    setJournalForm((prev) => ({
      ...prev,
      lines: prev.lines.map((line, lineIndex) => lineIndex === index ? { ...line, [field]: value } : line)
    }));
  };

  const addLine = () => {
    setJournalForm((prev) => ({ ...prev, lines: [...prev.lines, createBlankLine('DEBIT')] }));
  };

  const removeLine = (index) => {
    setJournalForm((prev) => {
      if (prev.lines.length <= 2) {
        return prev;
      }
      return {
        ...prev,
        lines: prev.lines.filter((_, lineIndex) => lineIndex !== index)
      };
    });
  };

  const resetJournalForm = () => {
    setJournalForm({
      entryDate: todayValue(),
      description: '',
      referenceType: '',
      referenceId: '',
      lines: [createBlankLine('DEBIT'), createBlankLine('CREDIT')]
    });
  };

  const handleCreateAccount = (e) => {
    e.preventDefault();
    setCreatingAccount(true);

    axios.post(`${API_BASE_URL}/api/accounting/accounts`, {
      name: accountForm.name,
      accountCode: accountForm.accountCode,
      category: accountForm.category,
      parentGroup: accountForm.parentGroup || null,
      description: accountForm.description || null
    })
      .then((res) => {
        toast.success(`Ledger ${res.data.accountCode} created.`);
        setAccountForm({
          name: '',
          accountCode: '',
          category: 'ASSET',
          parentGroup: '',
          description: ''
        });
        loadDashboard();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || `Failed to create ledger: ${err.message}`);
      })
      .finally(() => setCreatingAccount(false));
  };

  const handleCreateJournal = (e) => {
    e.preventDefault();
    setPostingJournal(true);

    const lines = journalForm.lines
      .filter((line) => line.accountId && line.amount)
      .map((line) => ({
        accountId: Number(line.accountId),
        entryType: line.entryType,
        amount: Number(line.amount),
        description: line.description || null
      }));

    axios.post(`${API_BASE_URL}/api/accounting/journals`, {
      entryDate: journalForm.entryDate,
      description: journalForm.description,
      referenceType: journalForm.referenceType || null,
      referenceId: journalForm.referenceId || null,
      sourceType: 'MANUAL',
      lines
    })
      .then((res) => {
        toast.success(`Journal ${res.data.entryNumber} posted.`);
        resetJournalForm();
        loadDashboard();
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || `Failed to post journal: ${err.message}`);
      })
      .finally(() => setPostingJournal(false));
  };

  const openLedger = (accountId) => {
    setLedgerModal({ open: true, loading: true, data: null });
    axios.get(`${API_BASE_URL}/api/accounting/accounts/${accountId}/ledger`)
      .then((res) => setLedgerModal({ open: true, loading: false, data: res.data }))
      .catch((err) => {
        toast.error(err.response?.data?.message || `Failed to load ledger: ${err.message}`);
        setLedgerModal({ open: false, loading: false, data: null });
      });
  };

  const debitDraftTotal = journalForm.lines.reduce((sum, line) => {
    return line.entryType === 'DEBIT' ? sum + amountValue(line.amount) : sum;
  }, 0);

  const creditDraftTotal = journalForm.lines.reduce((sum, line) => {
    return line.entryType === 'CREDIT' ? sum + amountValue(line.amount) : sum;
  }, 0);

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-r from-cyan-950/50 via-slate-900 to-slate-950 p-8">
        <div className="absolute right-0 top-0 h-64 w-64 rounded-full bg-cyan-400/10 blur-[90px]"></div>
        <div className="relative z-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.35em] text-cyan-300/80">Bookkeeping & Ledgers</p>
            <h2 className="mb-3 text-3xl font-bold text-white">Chart of accounts, journals, and double-entry posting</h2>
            <p className="text-sm text-slate-300">
              Create ledger accounts, record balanced journal vouchers, and audit every posting from one workspace.
            </p>
          </div>
          <button
            type="button"
            onClick={loadDashboard}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-cyan-400/20 bg-cyan-500/10 px-4 py-2.5 text-sm font-semibold text-cyan-200 transition-colors hover:bg-cyan-500/20"
          >
            Refresh Books
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Total Ledgers</p>
          <p className="mt-3 text-3xl font-bold text-white">{dashboard.totalAccounts || 0}</p>
          <p className="mt-2 text-xs text-slate-400">Default chart accounts and custom ledgers</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Journal Entries</p>
          <p className="mt-3 text-3xl font-bold text-white">{dashboard.totalJournalEntries || 0}</p>
          <p className="mt-2 text-xs text-slate-400">Posted manual and system-generated vouchers</p>
        </div>
        <div className="rounded-2xl border border-slate-800 bg-slate-900/50 p-5">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Posted Volume</p>
          <p className="mt-3 text-lg font-bold text-emerald-400">{formatCurrency(dashboard.postedDebitTotal || 0)}</p>
          <p className="mt-1 text-xs text-slate-400">Debits</p>
          <p className="mt-3 text-lg font-bold text-cyan-300">{formatCurrency(dashboard.postedCreditTotal || 0)}</p>
          <p className="mt-1 text-xs text-slate-400">Credits</p>
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_1.4fr]">
        <form onSubmit={handleCreateAccount} className="rounded-3xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-white">Create Ledger</h3>
              <p className="mt-1 text-sm text-slate-400">Add a new account to the chart of accounts.</p>
            </div>
            <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-cyan-200">
              Chart Of Accounts
            </span>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Ledger Name</label>
              <input value={accountForm.name} onChange={(e) => updateAccountForm('name', e.target.value)} required className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="Bank Charges" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Account Code</label>
              <input value={accountForm.accountCode} onChange={(e) => updateAccountForm('accountCode', e.target.value.toUpperCase())} required className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="5100" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Category</label>
              <select value={accountForm.category} onChange={(e) => updateAccountForm('category', e.target.value)} className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none">
                {ACCOUNT_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Parent Group</label>
              <input value={accountForm.parentGroup} onChange={(e) => updateAccountForm('parentGroup', e.target.value)} className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="Operating Expenses" />
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Description</label>
              <textarea value={accountForm.description} onChange={(e) => updateAccountForm('description', e.target.value)} rows="4" className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="Used for monthly bank fees or bookkeeping adjustments." />
            </div>
          </div>

          <button type="submit" disabled={creatingAccount} className="mt-6 inline-flex w-full items-center justify-center rounded-xl bg-cyan-500 px-4 py-3 font-semibold text-slate-950 transition-colors hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60">
            {creatingAccount ? 'Creating Ledger...' : 'Create Ledger'}
          </button>
        </form>

        <form onSubmit={handleCreateJournal} className="rounded-3xl border border-slate-800 bg-slate-900/50 p-6">
          <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">Record Journal Entry</h3>
              <p className="mt-1 text-sm text-slate-400">The journal will post only when total debits equal total credits.</p>
            </div>
            <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-xs text-emerald-300">
              <div>Debits: <span className="font-bold">{formatCurrency(debitDraftTotal)}</span></div>
              <div className="mt-1">Credits: <span className="font-bold">{formatCurrency(creditDraftTotal)}</span></div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Entry Date</label>
              <input type="date" value={journalForm.entryDate} onChange={(e) => updateJournalForm('entryDate', e.target.value)} className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" />
            </div>
            <div>
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Reference Type</label>
              <input value={journalForm.referenceType} onChange={(e) => updateJournalForm('referenceType', e.target.value)} className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="MANUAL_ADJUSTMENT" />
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Narration</label>
              <input value={journalForm.description} onChange={(e) => updateJournalForm('description', e.target.value)} required className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="Record stationery expense paid by bank." />
            </div>
            <div className="md:col-span-2">
              <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">Reference Id</label>
              <input value={journalForm.referenceId} onChange={(e) => updateJournalForm('referenceId', e.target.value)} className="w-full rounded-xl border border-slate-800 bg-slate-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="JV-REF-001" />
            </div>
          </div>

          <div className="mt-6 space-y-4">
            {journalForm.lines.map((line, index) => (
              <div key={index} className="rounded-2xl border border-slate-800 bg-slate-950/70 p-4">
                <div className="mb-4 flex items-center justify-between gap-3">
                  <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-500">Line {index + 1}</span>
                  {journalForm.lines.length > 2 && (
                    <button type="button" onClick={() => removeLine(index)} className="text-xs font-semibold text-rose-300 transition-colors hover:text-rose-200">
                      Remove
                    </button>
                  )}
                </div>
                <div className="grid gap-3 md:grid-cols-[1fr_150px_140px]">
                  <select value={line.accountId} onChange={(e) => updateLine(index, 'accountId', e.target.value)} required className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none">
                    <option value="">Select Ledger...</option>
                    {(dashboard.accounts || []).filter((account) => account.active).map((account) => (
                      <option key={account.id} value={account.id}>{account.accountCode} - {account.name}</option>
                    ))}
                  </select>
                  <select value={line.entryType} onChange={(e) => updateLine(index, 'entryType', e.target.value)} className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none">
                    {ENTRY_TYPES.map((type) => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                  <input type="number" min="0" step="0.01" value={line.amount} onChange={(e) => updateLine(index, 'amount', e.target.value)} required className="rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="0.00" />
                </div>
                <input value={line.description} onChange={(e) => updateLine(index, 'description', e.target.value)} className="mt-3 w-full rounded-xl border border-slate-800 bg-slate-900 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none" placeholder="Optional line description" />
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <button type="button" onClick={addLine} className="inline-flex items-center justify-center rounded-xl border border-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-200 transition-colors hover:bg-slate-800">
              Add Another Line
            </button>
            <button type="submit" disabled={postingJournal} className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-5 py-3 font-semibold text-white transition-colors hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60">
              {postingJournal ? 'Posting Journal...' : 'Post Journal Entry'}
            </button>
          </div>
        </form>
      </div>

      <div className="rounded-3xl border border-slate-800 bg-slate-900/50">
        <div className="flex items-center justify-between gap-4 border-b border-slate-800 px-6 py-5">
          <div>
            <h3 className="text-xl font-bold text-white">Ledger Directory</h3>
            <p className="mt-1 text-sm text-slate-400">Open any account to inspect its ledger postings.</p>
          </div>
          {loading && <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Loading...</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-950/40">
              <tr className="text-[11px] uppercase tracking-[0.2em] text-slate-500">
                <th className="px-6 py-4">Code</th>
                <th className="px-6 py-4">Ledger</th>
                <th className="px-6 py-4">Category</th>
                <th className="px-6 py-4">Balance</th>
                <th className="px-6 py-4">Type</th>
                <th className="px-6 py-4 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {(dashboard.accounts || []).map((account) => (
                <tr key={account.id} className="hover:bg-slate-800/20">
                  <td className="px-6 py-4 text-sm font-bold text-cyan-300">{account.accountCode}</td>
                  <td className="px-6 py-4">
                    <div className="font-semibold text-white">{account.name}</div>
                    <div className="mt-1 text-xs text-slate-500">{account.parentGroup || 'General Group'}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-300">{account.category}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-emerald-300">
                    {formatCurrency(account.currentBalance)} {account.balanceSide === 'DEBIT' ? 'Dr' : 'Cr'}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider ${account.systemGenerated ? 'bg-cyan-500/10 text-cyan-200' : 'bg-slate-800 text-slate-300'}`}>
                      {account.systemGenerated ? 'System' : 'Custom'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button type="button" onClick={() => openLedger(account.id)} className="rounded-lg border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition-colors hover:bg-slate-800">
                      View Ledger
                    </button>
                  </td>
                </tr>
              ))}
              {!loading && (!dashboard.accounts || dashboard.accounts.length === 0) && (
                <tr>
                  <td colSpan="6" className="px-6 py-10 text-center text-sm text-slate-500">No ledger accounts available yet.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="rounded-3xl border border-slate-800 bg-slate-900/50">
        <div className="border-b border-slate-800 px-6 py-5">
          <h3 className="text-xl font-bold text-white">Recent Journal Entries</h3>
          <p className="mt-1 text-sm text-slate-400">Most recent manual and system-posted accounting entries.</p>
        </div>
        <div className="space-y-4 p-6">
          {(dashboard.recentEntries || []).map((entry) => (
            <div key={entry.id} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="text-sm font-bold text-white">{entry.entryNumber}</span>
                    <span className="rounded-full bg-slate-800 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-300">
                      {entry.sourceType}
                    </span>
                    <span className="text-xs text-slate-500">{entry.entryDate}</span>
                  </div>
                  <p className="mt-3 text-sm text-slate-300">{entry.description}</p>
                  {(entry.referenceType || entry.referenceId) && (
                    <p className="mt-2 text-xs text-slate-500">
                      {entry.referenceType || 'REFERENCE'} {entry.referenceId ? `- ${entry.referenceId}` : ''}
                    </p>
                  )}
                </div>
                <div className="text-sm text-slate-300">
                  <div>Debit: <span className="font-bold text-emerald-300">{formatCurrency(entry.totalDebit)}</span></div>
                  <div className="mt-1">Credit: <span className="font-bold text-cyan-300">{formatCurrency(entry.totalCredit)}</span></div>
                </div>
              </div>
              <div className="mt-4 grid gap-2">
                {(entry.lines || []).map((line) => (
                  <div key={line.id} className="flex flex-col gap-1 rounded-xl border border-slate-800 bg-slate-900/70 px-4 py-3 text-sm md:flex-row md:items-center md:justify-between">
                    <div>
                      <span className="font-semibold text-white">{line.accountCode} - {line.accountName}</span>
                      {line.description && <span className="ml-2 text-slate-500">{line.description}</span>}
                    </div>
                    <div className={`${line.entryType === 'DEBIT' ? 'text-emerald-300' : 'text-cyan-300'} font-bold`}>
                      {line.entryType} {formatCurrency(line.amount)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
          {!loading && (!dashboard.recentEntries || dashboard.recentEntries.length === 0) && (
            <div className="rounded-2xl border border-dashed border-slate-800 px-6 py-12 text-center text-sm text-slate-500">
              No journal entries posted yet.
            </div>
          )}
        </div>
      </div>

      {ledgerModal.open && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/85 px-4 backdrop-blur-sm">
          <div className="max-h-[80vh] w-full max-w-4xl overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 shadow-2xl">
            <div className="flex items-start justify-between gap-4 border-b border-slate-800 px-6 py-5">
              <div>
                <h3 className="text-xl font-bold text-white">
                  {ledgerModal.data?.account?.accountCode} - {ledgerModal.data?.account?.name || 'Ledger'}
                </h3>
                <p className="mt-1 text-sm text-slate-400">
                  Balance: {formatCurrency(ledgerModal.data?.account?.currentBalance)} {ledgerModal.data?.account?.balanceSide === 'DEBIT' ? 'Dr' : 'Cr'}
                </p>
              </div>
              <button type="button" onClick={() => setLedgerModal({ open: false, loading: false, data: null })} className="rounded-lg border border-slate-700 px-3 py-2 text-sm font-semibold text-slate-300 transition-colors hover:bg-slate-800">
                Close
              </button>
            </div>

            <div className="max-h-[62vh] overflow-y-auto p-6">
              {ledgerModal.loading ? (
                <div className="py-16 text-center text-sm text-slate-500">Loading ledger...</div>
              ) : (
                <div className="space-y-3">
                  {(ledgerModal.data?.transactions || []).map((line, index) => (
                    <div key={`${line.journalEntryId}-${index}`} className="rounded-2xl border border-slate-800 bg-slate-950/60 p-4">
                      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                          <div className="font-semibold text-white">{line.entryNumber}</div>
                          <div className="mt-1 text-xs text-slate-500">{line.entryDate} {line.referenceId ? `- ${line.referenceId}` : ''}</div>
                          <p className="mt-2 text-sm text-slate-300">{line.description}</p>
                          {line.lineDescription && <p className="mt-1 text-xs text-slate-500">{line.lineDescription}</p>}
                        </div>
                        <div className={`${line.entryType === 'DEBIT' ? 'text-emerald-300' : 'text-cyan-300'} text-sm font-bold`}>
                          {line.entryType} {formatCurrency(line.amount)}
                        </div>
                      </div>
                    </div>
                  ))}
                  {(!ledgerModal.data?.transactions || ledgerModal.data.transactions.length === 0) && (
                    <div className="rounded-2xl border border-dashed border-slate-800 px-6 py-12 text-center text-sm text-slate-500">
                      No postings found for this ledger yet.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
